import React, { useState, useMemo } from 'react';
import {
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap';

const METRICS_BY_MODULE = {
  laboral: ['documents', 'hirings', 'signers'],
  corporate: ['documents', 'signers'],
  abstract: ['documents', 'signers'],
};

const DashboardMetrics = (props) => {
  const { customer, roles } = props;

  const enabledLaboralModule = useMemo(() => {
    return (
      customer.enabled_laboral_module &&
      (roles?.laboral?.document || roles?.laboral?.hiring || roles?.laboral?.signer)
    );
  }, [roles, customer]);

  const enabledCorporateModule = useMemo(() => {
    return (
      customer.enabled_corporate_module &&
      (roles?.corporate?.document || roles?.corporate?.signer)
    );
  }, [roles, customer]);

  const enabledModules = useMemo(() => {
    let modules = [];

    if (enabledLaboralModule) {
      modules.push({
        name: I18n.t('common.dashboard.sidebar.laboral'),
        icon: 'fas fa-briefcase',
        codeName: 'laboral',
        type: 'laboral',
        routes: {
          documentReportPath: Routes.report_laboral_hiring_documents_path(),
          hiringReportPath: Routes.report_laboral_hirings_path(),
          signerReportPath: Routes.report_laboral_signers_path(),
        },
      });
    }

    if (enabledCorporateModule) {
      modules.push({
        name: I18n.t('common.dashboard.sidebar.corporate'),
        icon: 'fas fa-city',
        codeName: 'corporate',
        type: 'corporate',
        routes: {
          documentReportPath: Routes.report_corporate_agreement_documents_path(),
          signerReportPath: Routes.report_corporate_signers_path(),
        },
      });
    }

    if (customer.enabled_abstract_module) {
      let abstractModules = _.sortBy(customer.abstract_modules, 'singular_name');

      _.each(abstractModules, (abstractModule) => {
        modules.push({
          name: abstractModule.singular_name,
          icon: abstractModule.module_icon,
          codeName: `abstract_module_${abstractModule.id}`,
          type: 'abstract',
          routes: {
            documentReportPath: Routes.abstract_report_documents_path(abstractModule.hashid),
            signerReportPath: Routes.abstract_report_signers_path(abstractModule.hashid),
          },
        });
      });
    }

    return modules;
  }, [enabledLaboralModule, enabledCorporateModule, customer]);

  const [activeTab, setActiveTab] = useState(enabledModules.length > 0 ? enabledModules[0].codeName : null);

  return (
    <Row>
      <Col md={12}>
        <DashboardMetricsNav
          enabledModules={enabledModules}
          activeTab={activeTab}
          callbacks={{
            setActiveTab: setActiveTab,
          }}
        />

        <DashboardMetricsTabContent enabledModules={enabledModules} activeTab={activeTab} />
      </Col>
    </Row>
  );
};

export default DashboardMetrics;

const DashboardMetricsNav = (props) => {
  const {
    enabledModules,
    activeTab,
    callbacks: { setActiveTab: callbackOnSetActiveTab },
  } = props;

  return (
    <Nav pills className="mb-4 dashboard-nav">
      {enabledModules.map((module) => (
        <NavItem key={`navItem-${module.codeName}`} className="text-center">
          <NavLink
            className={activeTab === module.codeName ? 'active' : ''}
            onClick={() => callbackOnSetActiveTab(module.codeName)}
          >
            <i className={`nav-icon mr-2 ${module.icon}`}></i>
            {module.name}
          </NavLink>
        </NavItem>
      ))}
    </Nav>
  );
};

const DashboardMetricsTabContent = (props) => {
  const { enabledModules, activeTab } = props;

  const renderMetricCard = (module, metricType) => {
    let title = '';
    let icon = '';
    let reportPath = '';

    switch (metricType) {
      case 'documents':
        title = I18n.t('activerecord.models.document.other');
        icon = 'fas fa-file-alt text-primary';
        reportPath = module.routes.documentReportPath || '#';
        break;
      case 'hirings':
        title = I18n.t('activerecord.models.laboral/hiring.other');
        icon = 'fas fa-address-book text-teal';
        reportPath = module.routes.hiringReportPath || '#';
        break;
      case 'signers':
        title = I18n.t('activerecord.models.signer.other');
        icon = 'fas fa-file-signature text-info';
        reportPath = module.routes.signerReportPath || '#';
        break;
      default:
        break;
    }

    // Use border color and text color based on metric type
    let borderColor = 'border-primary';
    let textColor = 'text-primary';
    let btnColor = 'bg-primary';

    if (metricType === 'hirings') {
      borderColor = 'border-teal';
      textColor = 'text-teal';
      btnColor = 'bg-teal';
    } else if (metricType === 'signers') {
      borderColor = 'border-info';
      textColor = 'text-info';
      btnColor = 'bg-info';
    }

    return (
      <Col sm={6} lg={6} xl={4} className="mb-4" key={`${module.codeName}-${metricType}`}>
        <div className={`small-box dashboard-box h-100 shadow-sm d-flex flex-column justify-content-between hover-scale-card ${borderColor}`}>
          <div className="icon">
            <i className={icon}></i>
          </div>
          <h4 className={`d-block font-weight-bold pb-3 ${textColor}`}>{title}</h4>
          <a href={reportPath} className={`mt-4 small-box-footer btn btn-block ${btnColor}`}>
            {I18n.t('actions.show_report')}
            &nbsp;<i className="fas fa-arrow-circle-right"></i>
          </a>
        </div>
      </Col>
    );
  };

  return (
    <TabContent activeTab={activeTab} className="dashboard-tab-content">
      {enabledModules.map((module) => (
        <TabPane tabId={module.codeName} key={`tabPane-${module.codeName}`}>
          <Row>
            {METRICS_BY_MODULE[module.type]?.map((metricType) => renderMetricCard(module, metricType))}
          </Row>
        </TabPane>
      ))}
    </TabContent>
  );
};
